// MissionReady Radio — authenticated server entry points for the live layer.
//
// The signed-in user's identity comes from the request bearer token, never from
// the client payload. Site authorization is enforced by the existing
// ensure_site_radio_channel / post_radio_transmission database functions
// (SECURITY DEFINER + has_site_access), so a client cannot join, signal on, or
// post into another site's Radio thread.
//
// Deepgram stays the transcription provider and its privileged key stays
// server-side: the client receives only a short-lived Deepgram access grant.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type RadioSessionGrant = {
  channel_id: string;
  user_id: string;
  /** Short-lived Deepgram streaming token; null when transcription is unavailable. */
  deepgram: { token: string; expires_in: number } | null;
  transcription_unavailable_reason: string | null;
  /** USE_LIVEKIT: per-site transport switch. "mesh" = legacy WebRTC mesh. */
  transport: "mesh" | "livekit";
  /** LiveKit join grant for room site-{siteId}; null on mesh sites. */
  livekit: { url: string; token: string; room: string; expires_in: number } | null;
};

const LIVEKIT_TTL_SECONDS = 60 * 60;

function b64url(input: ArrayBuffer | Uint8Array | string): string {
  const bytes =
    typeof input === "string" ? new TextEncoder().encode(input) : new Uint8Array(input as ArrayBuffer);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Mint a LiveKit access token (HS256) with Web Crypto — the secret never leaves the server. */
async function livekitToken(args: {
  apiKey: string;
  apiSecret: string;
  identity: string;
  name: string;
  room: string;
}): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const header = b64url(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const payload = b64url(
    JSON.stringify({
      iss: args.apiKey,
      sub: args.identity,
      name: args.name,
      nbf: now - 10,
      exp: now + LIVEKIT_TTL_SECONDS,
      video: {
        room: args.room,
        roomJoin: true,
        canPublish: true,
        canSubscribe: true,
        canPublishData: false,
        canPublishSources: ["microphone"],
      },
    }),
  );
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(args.apiSecret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(`${header}.${payload}`));
  return `${header}.${payload}.${b64url(sig)}`;
}

async function deepgramGrant(): Promise<RadioSessionGrant["deepgram"]> {
  const key = process.env["DEEPGRAM_API_KEY"];
  if (!key) return null;
  const res = await fetch("https://api.deepgram.com/v1/auth/grant", {
    method: "POST",
    headers: { Authorization: `Token ${key}`, "Content-Type": "application/json" },
    body: JSON.stringify({ ttl_seconds: 60 }),
  });
  if (!res.ok) {
    console.warn("[radio] deepgram grant failed", res.status);
    return null;
  }
  const json = (await res.json()) as { access_token?: string; expires_in?: number };
  if (!json.access_token) return null;
  return { token: json.access_token, expires_in: json.expires_in ?? 30 };
}

/**
 * Resolve (find-or-create) the authorized site Radio thread for the caller and
 * hand back a short-lived Deepgram streaming grant.
 */
export const startRadioSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        organization_id: z.string().uuid(),
        site_id: z.string().uuid(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }): Promise<RadioSessionGrant> => {
    const { supabase, userId } = context as any;
    const { data: channelId, error } = await supabase.rpc("ensure_site_radio_channel", {
      _org: data.organization_id,
      _site: data.site_id,
    });
    if (error || !channelId) {
      throw new Error(error?.message || "Not authorized for this site's Radio");
    }
    let deepgram: RadioSessionGrant["deepgram"] = null;
    let reason: string | null = null;
    try {
      deepgram = await deepgramGrant();
      if (!deepgram) reason = "Live transcription unavailable";
    } catch (e) {
      console.warn("[radio] deepgram grant error", e);
      reason = "Live transcription unavailable";
    }
    // USE_LIVEKIT per site (default off). Authorization was already enforced by
    // ensure_site_radio_channel (has_site_access) above, so the room is always
    // a site this caller may use.
    let transport: RadioSessionGrant["transport"] = "mesh";
    let livekit: RadioSessionGrant["livekit"] = null;
    const { data: site } = await supabase
      .from("sites")
      .select("radio_use_livekit")
      .eq("id", data.site_id)
      .maybeSingle();
    if (site?.radio_use_livekit) {
      const url = process.env["LIVEKIT_URL"];
      const apiKey = process.env["LIVEKIT_API_KEY"];
      const apiSecret = process.env["LIVEKIT_API_SECRET"];
      if (!url || !apiKey || !apiSecret) {
        throw new Error("Radio connection unavailable — LiveKit is not configured");
      }
      const claims = (context as any).claims ?? {};
      const name =
        claims.user_metadata?.full_name || claims.email || "Officer";
      const room = `site-${data.site_id}`;
      transport = "livekit";
      livekit = {
        url,
        room,
        expires_in: LIVEKIT_TTL_SECONDS,
        token: await livekitToken({ apiKey, apiSecret, identity: String(userId), name, room }),
      };
    }
    return {
      channel_id: String(channelId),
      user_id: String(userId),
      deepgram,
      transcription_unavailable_reason: reason,
      transport,
      livekit,
    };
  });

/**
 * Persist exactly ONE final Radio transcript for a completed transmission.
 * Interim results are never written. Raw audio is never stored.
 */
export const saveRadioTransmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        channel_id: z.string().uuid(),
        transcript: z.string().min(1).max(8000),
        started_at: z.string().min(1),
        ended_at: z.string().min(1),
        radio_channel: z.string().min(1).max(64).default("primary"),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context as any;
    const { data: messageId, error } = await supabase.rpc("post_radio_transmission", {
      _channel: data.channel_id,
      _transcript: data.transcript.trim(),
      _started_at: data.started_at,
      _ended_at: data.ended_at,
      _radio_channel: data.radio_channel,
    });
    if (error) throw new Error(error.message);
    return { message_id: Number(messageId) };
  });

const floorInput = (input: unknown) => z.object({
  channel_id: z.string().uuid(),
  token: z.string().uuid(),
}).parse(input);

export const claimRadioFloor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(floorInput)
  .handler(async ({ data, context }) => {
    const { data: claimed, error } = await context.supabase.rpc("claim_radio_floor", {
      _channel: data.channel_id, _token: data.token,
    });
    if (error) throw new Error(error.message);
    return claimed === true;
  });

export const renewRadioFloor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(floorInput)
  .handler(async ({ data, context }) => {
    const { data: renewed, error } = await context.supabase.rpc("renew_radio_floor", {
      _channel: data.channel_id, _token: data.token,
    });
    if (error) throw new Error(error.message);
    return renewed === true;
  });

export const releaseRadioFloor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(floorInput)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("release_radio_floor", {
      _channel: data.channel_id, _token: data.token,
    });
    if (error) throw new Error(error.message);
  });
